/* eslint-disable complexity */
import BigNumber from 'bignumber.js';
import Service from 'react-reservice';

import accountDeposit from '@webtrader/core/api/account/deposit';
import demoAccountCreationAPI, { IDemoAccountCreationResponse } from '@webtrader/core/api/user/demoAccount';
import { ELoginSource } from '@webtrader/core/api/user/types';
import appsflyerSDK from '@webtrader/core/appsflyer';
import { buildAttributionFields } from '@webtrader/core/appsflyer/deepLinkUtils';
import { getDialCodeFromCountryKey } from '@webtrader/core/format/country';
import i18n from '@webtrader/core/i18n';
import { IApiError } from '@webtrader/core/request/api';
import { mapResponseCodeToMessage } from '@webtrader/core/request/message';
import { isValidEmail, validatePhoneNumber } from '@webtrader/core/validators';

export interface IDemoCreationFormValues {
  firstName?: string;
  lastName?: string;
  email?: string;
  country?: string;
  countryKey?: string;
  mobile?: string;
  deposit?: number;
}

interface IDemoCreationContext {
  loading: boolean;
  values: IDemoCreationFormValues;
  showFirstNameValidation: boolean;
  showLastNameValidation: boolean;
  showEmailValidation: boolean;
  showCountryValidation: boolean;
  showMobileValidation: boolean;
  showDepositValidation: boolean;
  submitting: boolean;
  demoAccountInfo?: IDemoAccountCreationResponse;
}

interface IOnPcSubmitParams {
  onComplete?: (success: boolean, message: string) => void;
  platform?: ELoginSource;
}

interface IOnMobileSubmitParams {
  onComplete?: (success: boolean, message: string) => void;
  platform?: ELoginSource;
}

const defaultContext: IDemoCreationContext = {
  loading: false,
  values: {},
  showFirstNameValidation: false,
  showLastNameValidation: false,
  showEmailValidation: false,
  showCountryValidation: false,
  showMobileValidation: false,
  showDepositValidation: false,
  submitting: false,
  demoAccountInfo: undefined
};

class DemoCreationService extends Service<IDemoCreationContext> {
  private accountDepositAbortController = new AbortController();

  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public fetchDemoDepositAmount = async () => {
    this.context.loading = true;

    try {
      this.accountDepositAbortController.abort();
      this.accountDepositAbortController = new AbortController();

      const { result } = await accountDeposit.getAccountDeposit(this.accountDepositAbortController.signal);

      if (result) {
        this.context.values = {
          ...this.context.values,
          deposit: result
        };
      }
    } catch (err) {
      const { code, message } = err as IApiError;

      throw new Error(mapResponseCodeToMessage(code, message));
    } finally {
      this.context.loading = false;
    }
  };

  public onPcSubmit = async (params: IOnPcSubmitParams, publicAuth?: boolean) => {
    const { onComplete, platform = ELoginSource.WEB } = params;
    const { firstName, lastName, email, country, countryKey, mobile, deposit } = this.context.values;
    const dialCode = getDialCodeFromCountryKey(countryKey);
    const fullMobile = !!dialCode && !!mobile ? `${dialCode} ${mobile}` : undefined;

    if (!firstName || !lastName || !email || !fullMobile || !deposit || !country) {
      return;
    }

    const { appsFlyerAttribute } = buildAttributionFields(appsflyerSDK.getAttributionData());

    const payload = {
      firstName,
      lastName,
      country,
      email,
      phoneNumber: fullMobile,
      depositAmount: deposit,
      loginSource: platform,
      appsFlyerAttribute
    };

    try {
      this.context.submitting = true;

      const { result } = publicAuth
        ? await demoAccountCreationAPI.demo(payload)
        : await demoAccountCreationAPI.demoAuth(payload);

      this.context.demoAccountInfo = result ?? undefined;

      if (result) {
        onComplete?.(true, '');
      } else {
        onComplete?.(false, i18n.t('Unexpected error'));
      }
    } catch (err) {
      const { code, message } = err as IApiError;

      onComplete?.(false, mapResponseCodeToMessage(code, message));
    } finally {
      this.context.submitting = false;
    }
  };

  public onMobileSubmit = async (params: IOnMobileSubmitParams, publicAuth?: boolean) => {
    const { onComplete, platform } = params;
    const { firstName, lastName, email, country, countryKey, mobile, deposit } = this.context.values;
    const dialCode = getDialCodeFromCountryKey(countryKey);
    const fullMobile = !!dialCode && !!mobile ? `${dialCode} ${mobile}` : undefined;

    if (!firstName || !lastName || !email || !fullMobile || !deposit || !country) {
      return;
    }

    const { appsFlyerAttribute } = buildAttributionFields(appsflyerSDK.getAttributionData());

    const payload = {
      firstName,
      lastName,
      country,
      email,
      phoneNumber: fullMobile,
      depositAmount: deposit,
      loginSource: platform,
      appsFlyerAttribute
    };

    try {
      this.context.submitting = true;

      const { result } = publicAuth
        ? await demoAccountCreationAPI.demo(payload)
        : await demoAccountCreationAPI.demoAuth(payload);

      this.context.demoAccountInfo = result ?? undefined;

      if (result) {
        onComplete?.(true, '');
      } else {
        onComplete?.(false, i18n.t('Unexpected error'));
      }
    } catch (err) {
      const { code, message } = err as IApiError;
      onComplete?.(false, mapResponseCodeToMessage(code, message));
    } finally {
      this.context.submitting = false;
    }
  };

  public reset = () => {
    this.context = {
      ...defaultContext
    };
  };

  public onFirstNameChange = (value?: string | number) => {
    this.context.showFirstNameValidation = !value;

    this.context.values = {
      ...this.context.values,
      firstName: value?.toString()
    };
  };

  public onLastNameChange = (value?: string | number) => {
    this.context.showLastNameValidation = !value;

    this.context.values = {
      ...this.context.values,
      lastName: value?.toString()
    };
  };

  public onEmailChange = (value?: string | number) => {
    if (!value || (value && !isValidEmail(value as string))) {
      this.context.showEmailValidation = true;
    } else {
      this.context.showEmailValidation = false;
    }

    this.context.values = {
      ...this.context.values,
      email: value?.toString()
    };
  };

  public onCountryChange = (value?: string) => {
    this.context.showCountryValidation = !value;

    this.context.values = {
      ...this.context.values,
      country: value
    };
  };

  public onDialCodeChange = (value?: string | number) => {
    const dialCode = getDialCodeFromCountryKey(value?.toString());
    const isValid = this.context.values.mobile && value && validatePhoneNumber(dialCode, this.context.values.mobile);

    this.context.showMobileValidation = !isValid;

    this.context.values = {
      ...this.context.values,
      countryKey: value?.toString()
    };
  };

  public onMobileChange = (value?: string | number) => {
    const dialCode = getDialCodeFromCountryKey(this.context.values.countryKey);
    const isValid = dialCode && value && validatePhoneNumber(dialCode, value.toString());

    this.context.showMobileValidation = !isValid;

    this.context.values = {
      ...this.context.values,
      mobile: value?.toString()
    };
  };

  public onDepositChange = (value?: number) => {
    if (!value) {
      this.context.showDepositValidation = true;
      return;
    }

    const valueBigNumber = new BigNumber(value);
    const isValid = !valueBigNumber.isNaN();

    this.context.showDepositValidation = !isValid;

    this.context.values = {
      ...this.context.values,
      deposit: value
    };
  };
}

export default new DemoCreationService();
